/**
 * Agrégations statistiques de l'espace pro (électricien).
 *
 * TypeScript pur, aucune I/O : les repositories renvoient des lignes plates,
 * tout le calcul vit ici et se teste sans backend.
 *
 * ⚠ Deux notions de commission cohabitent, ne jamais les confondre :
 *  - `pro_commandes.total_commission` = la commission PROMISE par la commande.
 *  - `pro_wallet_movements` (type `commission`) = la commission RÉELLEMENT
 *    créditée, en attente ou validée par l'admin. C'est elle qui fait l'argent.
 * Le portefeuille se lit donc TOUJOURS sur le grand livre, jamais sur les
 * commandes (une commande éditée, rejetée ou retournée diverge de son mouvement).
 */

import { round3 } from './pricing';
import type {
  CommandeStatus,
  DevisStatus,
  WalletMovementStatus,
  WalletMovementType,
} from './types';

// ============================================================
// Entrées — formes minimales, découplées des DTO data-access
// ============================================================

export interface ProCommandeStatRow {
  code: string;
  /** Jour calendaire `YYYY-MM-DD`. */
  date: string;
  status: CommandeStatus;
  proClientId: string | null;
  /** Devis d'origine, quand la commande vient d'une transformation. */
  parentDevisCode: string | null;
  /** Total TTC marchandise (hors livraison). */
  pttc: number;
  totalCommission: number;
  deliveryFee: number;
  /** Part réglée avec le portefeuille. */
  totalUsed: number;
  /** Reste à payer en cash par le client. */
  totalAPayer: number;
}

/** En-tête enrichi utilisé par le tableau de bord du fournisseur. */
export interface ProTenantCommandeStatRow extends ProCommandeStatRow {
  /** Compte électricien qui a passé la commande. */
  proCustomerId: string;
  /** Ville de livraison librement saisie. */
  deliveryCity: string | null;
}

export interface ProDevisStatRow {
  code: string;
  date: string;
  status: DevisStatus;
  proClientId: string | null;
  pttc: number;
  totalCommission: number;
}

export interface ProWalletStatRow {
  amount: number;
  type: WalletMovementType;
  status: WalletMovementStatus;
  date: string;
}

// ============================================================
// Périodes
// ============================================================

export type ProStatsRange =
  | 'this_week'
  | 'this_month'
  | 'last_month'
  | 'last_3m'
  | 'last_6m'
  | 'last_12m'
  | 'this_year'
  | 'all';

export interface ProDateBounds {
  /** Inclusif. `null` = pas de borne basse. */
  from: Date | null;
  /** EXCLUSIF. `null` = pas de borne haute. */
  to: Date | null;
}

/**
 * Borne une période en dates LOCALES.
 *
 * Semaine tunisienne : lundi → dimanche.
 * `to` est exclusif pour que « ce mois » n'attrape jamais le 1er du suivant.
 */
export function resolveStatsRange(
  range: ProStatsRange,
  now: Date = new Date(),
): ProDateBounds {
  const y = now.getFullYear();
  const m = now.getMonth();
  // Borne haute exclusive : demain minuit, pour que « aujourd'hui » soit inclus.
  const tomorrow = new Date(y, m, now.getDate() + 1);

  switch (range) {
    case 'this_week': {
      // getDay() : 0 = dimanche → on décale pour que lundi vaille 0.
      const mondayOffset = (now.getDay() + 6) % 7;
      const monday = new Date(y, m, now.getDate() - mondayOffset);
      return { from: monday, to: tomorrow };
    }
    case 'this_month':
      return { from: new Date(y, m, 1), to: tomorrow };
    case 'last_month':
      return { from: new Date(y, m - 1, 1), to: new Date(y, m, 1) };
    case 'last_3m':
      return { from: new Date(y, m - 2, 1), to: tomorrow };
    case 'last_6m':
      return { from: new Date(y, m - 5, 1), to: tomorrow };
    case 'last_12m':
      return { from: new Date(y, m - 11, 1), to: tomorrow };
    case 'this_year':
      return { from: new Date(y, 0, 1), to: tomorrow };
    case 'all':
    default:
      return { from: null, to: null };
  }
}

/**
 * Recule une date de `delta` mois en bornant le jour au dernier du mois cible.
 *
 * `new Date(y, m - 1, 31)` déborde silencieusement (31 mars − 1 mois = 3 mars) :
 * une comparaison mensuelle atterrirait alors dans le mauvais mois.
 */
function shiftMonths(date: Date, delta: number): Date {
  const target = new Date(date.getFullYear(), date.getMonth() + delta, 1);
  const lastDay = new Date(
    target.getFullYear(),
    target.getMonth() + 1,
    0,
  ).getDate();
  return new Date(
    target.getFullYear(),
    target.getMonth(),
    Math.min(date.getDate(), lastDay),
  );
}

/**
 * Fenêtre de comparaison : la même période, un cran plus tôt.
 *
 * On recule la date de référence d'un pas naturel (une semaine, un mois, un an)
 * puis on ré-applique la même règle, plutôt que de soustraire une durée brute :
 * « cette semaine » un mercredi couvre lundi→mercredi, et doit se comparer au
 * lundi→mercredi PRÉCÉDENT — pas au vendredi→dimanche d'avant, qui serait à la
 * fois désaligné et sur des jours d'activité différents.
 *
 * `all` n'a pas de précédent : renvoie `null` et l'appelant masque l'écart
 * plutôt que d'inventer une variation.
 */
export function resolvePreviousStatsRange(
  range: ProStatsRange,
  now: Date = new Date(),
): ProDateBounds | null {
  switch (range) {
    case 'this_week':
      return resolveStatsRange(
        'this_week',
        new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7),
      );
    case 'this_month':
      return resolveStatsRange('this_month', shiftMonths(now, -1));
    case 'last_month':
      return resolveStatsRange('last_month', shiftMonths(now, -1));
    case 'last_3m':
      return resolveStatsRange('last_3m', shiftMonths(now, -3));
    case 'last_6m':
      return resolveStatsRange('last_6m', shiftMonths(now, -6));
    case 'last_12m':
      return resolveStatsRange('last_12m', shiftMonths(now, -12));
    case 'this_year':
      return resolveStatsRange(
        'this_year',
        new Date(now.getFullYear() - 1, now.getMonth(), now.getDate()),
      );
    case 'all':
    default:
      return null;
  }
}

/**
 * Variation en % entre deux valeurs.
 *
 * `null` quand la base est nulle : passer de 0 à 3 commandes n'est pas
 * « +300 % », c'est une variation non définie — l'UI affiche alors un tiret.
 */
export function percentChange(
  current: number,
  previous: number,
): number | null {
  if (!Number.isFinite(current) || !Number.isFinite(previous)) return null;
  if (previous === 0) return null;
  return round3(((current - previous) / Math.abs(previous)) * 100);
}

/**
 * Parse un jour calendaire `YYYY-MM-DD` en date LOCALE.
 *
 * `new Date('2026-08-01')` serait interprété en UTC : à minuit passé en Tunisie
 * (UTC+1) la comparaison bascule d'un jour et un document sort de sa période.
 */
export function parseCalendarDay(
  value: string | null | undefined,
): Date | null {
  if (!value) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (match) {
    return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
}

export function isWithinBounds(
  date: string | null | undefined,
  bounds: ProDateBounds,
): boolean {
  if (!bounds.from && !bounds.to) return true;
  const day = parseCalendarDay(date);
  if (!day) return false;
  if (bounds.from && day < bounds.from) return false;
  if (bounds.to && day >= bounds.to) return false;
  return true;
}

/** Filtre générique sur le champ `date` d'une ligne datée. */
export function filterByRange<T extends { date: string }>(
  rows: T[],
  bounds: ProDateBounds,
): T[] {
  if (!bounds.from && !bounds.to) return rows;
  return rows.filter((row) => isWithinBounds(row.date, bounds));
}

// ============================================================
// Portefeuille
// ============================================================

export interface ProWalletSummary {
  /**
   * Solde dépensable aujourd'hui = somme de TOUS les mouvements validés
   * (commissions créditées moins utilisations et retraits). Reproduit
   * `pro_customer_wallet_view.balance`.
   */
  available: number;
  /** Commission validée par l'admin (cumul crédité, avant dépense). */
  validated: number;
  /** Commission en attente de validation. */
  pending: number;
  /** Commission refusée par l'admin. */
  rejected: number;
  /** Total gagné quel qu'en soit l'état = validée + en attente. */
  earned: number;
  /** Portefeuille dépensé sur des commandes, en valeur POSITIVE. */
  used: number;
  /** Retraits en espèces, en valeur POSITIVE. */
  withdrawn: number;
  /** Ajustements manuels de l'admin (signés). */
  adjustments: number;
  /** Nombre de mouvements du grand livre sur la période. */
  movementsCount: number;
}

export function computeWalletSummary(
  movements: ProWalletStatRow[],
): ProWalletSummary {
  let available = 0;
  let validated = 0;
  let pending = 0;
  let rejected = 0;
  let used = 0;
  let withdrawn = 0;
  let adjustments = 0;

  for (const movement of movements) {
    const amount = Number(movement.amount) || 0;
    if (movement.status === 'approved') available += amount;

    if (movement.type === 'commission') {
      if (movement.status === 'approved') validated += amount;
      else if (movement.status === 'pending') pending += amount;
      else rejected += amount;
      continue;
    }

    if (movement.status !== 'approved') continue;
    // `use` et `withdrawal` sont stockés négatifs : on les expose en positif.
    if (movement.type === 'use') used += Math.abs(amount);
    else if (movement.type === 'withdrawal') withdrawn += Math.abs(amount);
    else adjustments += amount;
  }

  return {
    available: round3(available),
    validated: round3(validated),
    pending: round3(pending),
    rejected: round3(rejected),
    earned: round3(validated + pending),
    used: round3(used),
    withdrawn: round3(withdrawn),
    adjustments: round3(adjustments),
    movementsCount: movements.length,
  };
}

// ============================================================
// Activité (commandes + devis)
// ============================================================

export interface ProActivitySummary {
  ordersCount: number;
  ordersPending: number;
  ordersDelivered: number;
  /** Rejetées + retournées : ni CA ni commission à espérer. */
  ordersCancelled: number;
  quotesCount: number;
  quotesPending: number;
  quotesValidated: number;
  quotesRejected: number;
  /** CA TTC marchandise (livraison exclue). */
  revenueTtc: number;
  /** Encaissé : ce que les clients paient réellement en cash. */
  collected: number;
  /** Réglé avec le portefeuille au lieu du cash. */
  paidWithWallet: number;
  deliveryFees: number;
  /** Commission promise par les commandes. */
  commissionOnOrders: number;
  /** Commission encore seulement chiffrée sur des devis non transformés. */
  commissionOnPendingQuotes: number;
  /** Montant TTC chiffré en devis. */
  quotedTtc: number;
  averageOrderValue: number;
  /** Marge commission / CA (%). */
  commissionRateOnRevenue: number;
  /** Devis transformés en commande / devis émis (%). */
  quoteConversionRate: number;
  /** Clients distincts touchés par un devis OU une commande. */
  clientsCount: number;
  /** Clients distincts ayant au moins une commande. */
  activeClientsCount: number;
}

const CANCELLED_ORDER_STATUSES: readonly CommandeStatus[] = [
  'rejetee',
  'retournee',
];

export function computeActivitySummary(
  commandes: ProCommandeStatRow[],
  devis: ProDevisStatRow[],
): ProActivitySummary {
  let revenueTtc = 0;
  let collected = 0;
  let paidWithWallet = 0;
  let deliveryFees = 0;
  let commissionOnOrders = 0;
  let ordersPending = 0;
  let ordersDelivered = 0;
  let ordersCancelled = 0;

  const clientIds = new Set<string>();
  const orderingClientIds = new Set<string>();
  const convertedQuoteCodes = new Set<string>();

  for (const order of commandes) {
    revenueTtc += Number(order.pttc) || 0;
    collected += Number(order.totalAPayer) || 0;
    paidWithWallet += Number(order.totalUsed) || 0;
    deliveryFees += Number(order.deliveryFee) || 0;
    commissionOnOrders += Number(order.totalCommission) || 0;

    if (order.status === 'en_attente') ordersPending += 1;
    if (order.status === 'livree') ordersDelivered += 1;
    if (CANCELLED_ORDER_STATUSES.includes(order.status)) ordersCancelled += 1;

    if (order.proClientId) {
      clientIds.add(order.proClientId);
      orderingClientIds.add(order.proClientId);
    }
    if (order.parentDevisCode) convertedQuoteCodes.add(order.parentDevisCode);
  }

  let quotedTtc = 0;
  let commissionOnPendingQuotes = 0;
  let quotesPending = 0;
  let quotesValidated = 0;
  let quotesRejected = 0;

  for (const quote of devis) {
    quotedTtc += Number(quote.pttc) || 0;
    if (quote.status === 'en_attente') quotesPending += 1;
    if (quote.status === 'valide') quotesValidated += 1;
    if (quote.status === 'rejetee') quotesRejected += 1;
    // Un devis déjà transformé a sa commission comptée sur la commande :
    // l'additionner ici la compterait deux fois.
    if (quote.status !== 'rejetee' && !convertedQuoteCodes.has(quote.code)) {
      commissionOnPendingQuotes += Number(quote.totalCommission) || 0;
    }
    if (quote.proClientId) clientIds.add(quote.proClientId);
  }

  const ordersCount = commandes.length;
  const quotesCount = devis.length;

  return {
    ordersCount,
    ordersPending,
    ordersDelivered,
    ordersCancelled,
    quotesCount,
    quotesPending,
    quotesValidated,
    quotesRejected,
    revenueTtc: round3(revenueTtc),
    collected: round3(collected),
    paidWithWallet: round3(paidWithWallet),
    deliveryFees: round3(deliveryFees),
    commissionOnOrders: round3(commissionOnOrders),
    commissionOnPendingQuotes: round3(commissionOnPendingQuotes),
    quotedTtc: round3(quotedTtc),
    averageOrderValue: ordersCount > 0 ? round3(revenueTtc / ordersCount) : 0,
    commissionRateOnRevenue:
      revenueTtc > 0 ? round3((commissionOnOrders / revenueTtc) * 100) : 0,
    quoteConversionRate:
      quotesCount > 0
        ? round3(
            (countConverted(devis, convertedQuoteCodes) / quotesCount) * 100,
          )
        : 0,
    clientsCount: clientIds.size,
    activeClientsCount: orderingClientIds.size,
  };
}

/** Ne compte que les devis réellement présents dans le jeu filtré. */
function countConverted(
  devis: ProDevisStatRow[],
  convertedCodes: Set<string>,
): number {
  let count = 0;
  for (const quote of devis) if (convertedCodes.has(quote.code)) count += 1;
  return count;
}

// ============================================================
// Séries mensuelles
// ============================================================

export interface ProMonthlyPoint {
  /** Clé `YYYY-MM` — le libellé lisible est formaté côté UI (i18n). */
  month: string;
  orders: number;
  /** Devis émis dans le mois (0 si la série est calculée sans les devis). */
  quotes: number;
  revenueTtc: number;
  /** Commission promise par les commandes du mois. */
  commission: number;
  /** Commission créditée et validée ce mois-là (grand livre). */
  commissionValidated: number;
  /** Commission créditée mais encore en attente. */
  commissionPending: number;
}

export interface ProDailyPoint {
  date: string;
  orders: number;
  revenueTtc: number;
  commission: number;
}

function calendarDayKey(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

/** Série journalière continue sur une période. Les jours sans commande restent
 * visibles à zéro pour que la courbe ne rapproche pas artificiellement deux
 * pics séparés par une semaine creuse. */
export function computeDailySeries(
  commandes: ProCommandeStatRow[],
  bounds: ProDateBounds,
): ProDailyPoint[] {
  const validDays = commandes
    .map((order) => parseCalendarDay(order.date))
    .filter((date): date is Date => date != null)
    .sort((a, b) => a.getTime() - b.getTime());
  const from = bounds.from ?? validDays[0];
  const lastDataDay = validDays[validDays.length - 1];
  const to =
    bounds.to ??
    (lastDataDay
      ? new Date(
          lastDataDay.getFullYear(),
          lastDataDay.getMonth(),
          lastDataDay.getDate() + 1,
        )
      : null);
  if (!from || !to || from >= to) return [];

  const buckets = new Map<string, ProDailyPoint>();
  for (const order of commandes) {
    if (!isWithinBounds(order.date, { from, to })) continue;
    const day = parseCalendarDay(order.date);
    if (!day) continue;
    const key = calendarDayKey(day);
    const point = buckets.get(key) ?? {
      date: key,
      orders: 0,
      revenueTtc: 0,
      commission: 0,
    };
    point.orders += 1;
    point.revenueTtc += Number(order.pttc) || 0;
    point.commission += Number(order.totalCommission) || 0;
    buckets.set(key, point);
  }

  const axis: ProDailyPoint[] = [];
  for (
    const cursor = new Date(from);
    cursor < to;
    cursor.setDate(cursor.getDate() + 1)
  ) {
    const key = calendarDayKey(cursor);
    const point = buckets.get(key);
    axis.push(
      point
        ? {
            ...point,
            revenueTtc: round3(point.revenueTtc),
            commission: round3(point.commission),
          }
        : { date: key, orders: 0, revenueTtc: 0, commission: 0 },
    );
  }
  return axis;
}

function monthKey(date: string): string | null {
  const day = parseCalendarDay(date);
  if (!day) return null;
  return `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, '0')}`;
}

function addMonths(
  year: number,
  monthIndex: number,
  delta: number,
): { key: string } {
  const d = new Date(year, monthIndex + delta, 1);
  return {
    key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
  };
}

/**
 * Série mensuelle sur un axe CONTINU : les mois sans activité valent 0 au lieu
 * d'être absents. Un axe troué écrase visuellement les creux et fait mentir la
 * courbe (deux mois côte à côte alors qu'il y a un trimestre entre les deux).
 *
 * `months = null` → de la première donnée jusqu'au mois courant.
 */
export function computeMonthlySeries(
  commandes: ProCommandeStatRow[],
  wallet: ProWalletStatRow[],
  options: { months: number | null; now?: Date; devis?: ProDevisStatRow[] },
): ProMonthlyPoint[] {
  const now = options.now ?? new Date();
  const buckets = new Map<string, ProMonthlyPoint>();

  const ensure = (key: string): ProMonthlyPoint => {
    let point = buckets.get(key);
    if (!point) {
      point = {
        month: key,
        orders: 0,
        quotes: 0,
        revenueTtc: 0,
        commission: 0,
        commissionValidated: 0,
        commissionPending: 0,
      };
      buckets.set(key, point);
    }
    return point;
  };

  for (const order of commandes) {
    const key = monthKey(order.date);
    if (!key) continue;
    const point = ensure(key);
    point.orders += 1;
    point.revenueTtc += Number(order.pttc) || 0;
    point.commission += Number(order.totalCommission) || 0;
  }

  for (const quote of options.devis ?? []) {
    const key = monthKey(quote.date);
    if (!key) continue;
    ensure(key).quotes += 1;
  }

  for (const movement of wallet) {
    if (movement.type !== 'commission') continue;
    const key = monthKey(movement.date);
    if (!key) continue;
    const point = ensure(key);
    const amount = Number(movement.amount) || 0;
    if (movement.status === 'approved') point.commissionValidated += amount;
    else if (movement.status === 'pending') point.commissionPending += amount;
  }

  // Axe continu : on borne, puis on remplit chaque mois manquant.
  const currentKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const keys = [...buckets.keys()].sort();
  const earliest = keys[0] ?? currentKey;

  const span =
    options.months != null
      ? options.months
      : monthsBetween(earliest, currentKey) + 1;
  const length = Math.max(1, span);

  const axis: ProMonthlyPoint[] = [];
  for (let i = length - 1; i >= 0; i -= 1) {
    const { key } = addMonths(now.getFullYear(), now.getMonth(), -i);
    const point = buckets.get(key);
    axis.push(
      point
        ? {
            ...point,
            revenueTtc: round3(point.revenueTtc),
            commission: round3(point.commission),
            commissionValidated: round3(point.commissionValidated),
            commissionPending: round3(point.commissionPending),
          }
        : {
            month: key,
            orders: 0,
            quotes: 0,
            revenueTtc: 0,
            commission: 0,
            commissionValidated: 0,
            commissionPending: 0,
          },
    );
  }
  return axis;
}

function monthsBetween(fromKey: string, toKey: string): number {
  const [fy, fm] = fromKey.split('-').map(Number);
  const [ty, tm] = toKey.split('-').map(Number);
  if (!fy || !fm || !ty || !tm) return 0;
  return Math.max(0, (ty - fy) * 12 + (tm - fm));
}

// ============================================================
// Répartitions
// ============================================================

export interface ProStatusSlice<S extends string> {
  status: S;
  count: number;
  /** Part du total, en % (0 quand il n'y a rien). */
  share: number;
  amountTtc: number;
}

export function computeStatusBreakdown<S extends string>(
  rows: Array<{ status: S; pttc: number }>,
  statuses: readonly S[],
): ProStatusSlice<S>[] {
  const counts = new Map<S, { count: number; amount: number }>();
  for (const status of statuses) counts.set(status, { count: 0, amount: 0 });
  for (const row of rows) {
    const bucket = counts.get(row.status);
    if (!bucket) continue;
    bucket.count += 1;
    bucket.amount += Number(row.pttc) || 0;
  }
  const total = rows.length;
  return statuses.map((status) => {
    const bucket = counts.get(status) ?? { count: 0, amount: 0 };
    return {
      status,
      count: bucket.count,
      share: total > 0 ? round3((bucket.count / total) * 100) : 0,
      amountTtc: round3(bucket.amount),
    };
  });
}

export interface ProWeekdayPoint {
  /** 0 = lundi … 6 = dimanche. */
  weekday: number;
  orders: number;
  revenueTtc: number;
}

/** Répartition des commandes par jour de semaine, lundi en tête. */
export function computeWeekdayDistribution(
  commandes: ProCommandeStatRow[],
): ProWeekdayPoint[] {
  const points: ProWeekdayPoint[] = Array.from({ length: 7 }, (_, weekday) => ({
    weekday,
    orders: 0,
    revenueTtc: 0,
  }));
  for (const order of commandes) {
    const day = parseCalendarDay(order.date);
    if (!day) continue;
    const weekday = (day.getDay() + 6) % 7;
    const point = points[weekday];
    if (!point) continue;
    point.orders += 1;
    point.revenueTtc += Number(order.pttc) || 0;
  }
  return points.map((point) => ({
    ...point,
    revenueTtc: round3(point.revenueTtc),
  }));
}

// ============================================================
// Tableau de bord fournisseur (activité par électricien / ville)
// ============================================================

export interface ProCustomerPerformanceRow {
  proCustomerId: string;
  ordersCount: number;
  revenueTtc: number;
  commission: number;
  averageOrderValue: number;
  revenueShare: number;
  lastOrderDate: string | null;
}

/** Classe les comptes électriciens par CA sur la période. */
export function computeProCustomerPerformance(
  commandes: ProTenantCommandeStatRow[],
): ProCustomerPerformanceRow[] {
  const rows = new Map<string, ProCustomerPerformanceRow>();
  let totalRevenue = 0;

  for (const order of commandes) {
    const revenue = Number(order.pttc) || 0;
    const commission = Number(order.totalCommission) || 0;
    const current = rows.get(order.proCustomerId) ?? {
      proCustomerId: order.proCustomerId,
      ordersCount: 0,
      revenueTtc: 0,
      commission: 0,
      averageOrderValue: 0,
      revenueShare: 0,
      lastOrderDate: null,
    };
    current.ordersCount += 1;
    current.revenueTtc += revenue;
    current.commission += commission;
    if (!current.lastOrderDate || order.date > current.lastOrderDate) {
      current.lastOrderDate = order.date;
    }
    rows.set(order.proCustomerId, current);
    totalRevenue += revenue;
  }

  return [...rows.values()]
    .map((row) => ({
      ...row,
      revenueTtc: round3(row.revenueTtc),
      commission: round3(row.commission),
      averageOrderValue:
        row.ordersCount > 0 ? round3(row.revenueTtc / row.ordersCount) : 0,
      revenueShare:
        totalRevenue > 0 ? round3((row.revenueTtc / totalRevenue) * 100) : 0,
    }))
    .sort(
      (a, b) => b.revenueTtc - a.revenueTtc || b.ordersCount - a.ordersCount,
    );
}

export interface ProDeliveryCityRow {
  city: string;
  orders: number;
  revenue: number;
}

/** Regroupe les villes saisies en ignorant casse et espaces superflus. */
export function computeProDeliveryCities(
  commandes: ProTenantCommandeStatRow[],
): ProDeliveryCityRow[] {
  const rows = new Map<string, ProDeliveryCityRow>();

  for (const order of commandes) {
    const city = order.deliveryCity?.trim();
    if (!city) continue;
    const key = city.toLocaleLowerCase('fr-TN');
    const current = rows.get(key) ?? { city, orders: 0, revenue: 0 };
    current.orders += 1;
    current.revenue += Number(order.pttc) || 0;
    rows.set(key, current);
  }

  return [...rows.values()]
    .map((row) => ({ ...row, revenue: round3(row.revenue) }))
    .sort((a, b) => b.revenue - a.revenue || b.orders - a.orders);
}

// ============================================================
// Statistiques par client
// ============================================================

/** Clé du seau « sans client » (commandes passées sans fiche client). */
export const UNASSIGNED_CLIENT_KEY = '__unassigned__';

export interface ProClientStatRow {
  /** `null` quand la commande n'était rattachée à aucune fiche client. */
  clientId: string | null;
  ordersCount: number;
  quotesCount: number;
  /** CA TTC des commandes de ce client. */
  revenueTtc: number;
  /** Encaissé en cash auprès de ce client. */
  collected: number;
  /** Part réglée avec le portefeuille de l'électricien. */
  paidWithWallet: number;
  /** Commission générée par ce client. */
  commission: number;
  /** Montant TTC chiffré en devis pour ce client. */
  quotedTtc: number;
  /** Devis encore en attente (opportunités ouvertes). */
  openQuotesCount: number;
  openQuotesTtc: number;
  firstOrderDate: string | null;
  lastOrderDate: string | null;
  /** Dernière activité, devis ou commande. */
  lastActivityDate: string | null;
  averageOrderValue: number;
  /** Part du CA total, en %. */
  revenueShare: number;
  /** Commission / CA de ce client, en %. */
  commissionRate: number;
}

/**
 * Agrège commandes et devis par client.
 *
 * Les colonnes `pro_clients.depense` / `.commission` / `.solde_cumule` existent
 * en base mais aucun trigger ne les alimente : elles valent 0 pour tout le
 * monde. Les chiffres par client se recalculent donc TOUJOURS ici, à partir des
 * documents — ne jamais lire ces colonnes.
 *
 * Trié par CA décroissant.
 */
export function computeClientStats(
  commandes: ProCommandeStatRow[],
  devis: ProDevisStatRow[],
): ProClientStatRow[] {
  const rows = new Map<string, ProClientStatRow>();

  const ensure = (clientId: string | null): ProClientStatRow => {
    const key = clientId ?? UNASSIGNED_CLIENT_KEY;
    let row = rows.get(key);
    if (!row) {
      row = {
        clientId,
        ordersCount: 0,
        quotesCount: 0,
        revenueTtc: 0,
        collected: 0,
        paidWithWallet: 0,
        commission: 0,
        quotedTtc: 0,
        openQuotesCount: 0,
        openQuotesTtc: 0,
        firstOrderDate: null,
        lastOrderDate: null,
        lastActivityDate: null,
        averageOrderValue: 0,
        revenueShare: 0,
        commissionRate: 0,
      };
      rows.set(key, row);
    }
    return row;
  };

  let totalRevenue = 0;

  for (const order of commandes) {
    const row = ensure(order.proClientId);
    row.ordersCount += 1;
    row.revenueTtc += Number(order.pttc) || 0;
    row.collected += Number(order.totalAPayer) || 0;
    row.paidWithWallet += Number(order.totalUsed) || 0;
    row.commission += Number(order.totalCommission) || 0;
    totalRevenue += Number(order.pttc) || 0;

    if (!row.firstOrderDate || order.date < row.firstOrderDate)
      row.firstOrderDate = order.date;
    if (!row.lastOrderDate || order.date > row.lastOrderDate)
      row.lastOrderDate = order.date;
    if (!row.lastActivityDate || order.date > row.lastActivityDate)
      row.lastActivityDate = order.date;
  }

  for (const quote of devis) {
    const row = ensure(quote.proClientId);
    row.quotesCount += 1;
    row.quotedTtc += Number(quote.pttc) || 0;
    if (quote.status === 'en_attente') {
      row.openQuotesCount += 1;
      row.openQuotesTtc += Number(quote.pttc) || 0;
    }
    if (!row.lastActivityDate || quote.date > row.lastActivityDate)
      row.lastActivityDate = quote.date;
  }

  return [...rows.values()]
    .map((row) => ({
      ...row,
      revenueTtc: round3(row.revenueTtc),
      collected: round3(row.collected),
      paidWithWallet: round3(row.paidWithWallet),
      commission: round3(row.commission),
      quotedTtc: round3(row.quotedTtc),
      openQuotesTtc: round3(row.openQuotesTtc),
      averageOrderValue:
        row.ordersCount > 0 ? round3(row.revenueTtc / row.ordersCount) : 0,
      revenueShare:
        totalRevenue > 0 ? round3((row.revenueTtc / totalRevenue) * 100) : 0,
      commissionRate:
        row.revenueTtc > 0
          ? round3((row.commission / row.revenueTtc) * 100)
          : 0,
    }))
    .sort(
      (a, b) => b.revenueTtc - a.revenueTtc || b.ordersCount - a.ordersCount,
    );
}

// ============================================================
// Vue d'ensemble
// ============================================================

export interface ProDashboardSnapshot {
  wallet: ProWalletSummary;
  activity: ProActivitySummary;
  clients: ProClientStatRow[];
}

/**
 * Instantané complet pour une période donnée.
 *
 * ⚠ Le portefeuille est volontairement calculé sur le grand livre ENTIER, pas
 * sur la période : un solde disponible n'a de sens que cumulé depuis l'origine.
 * Seules l'activité et la ventilation client suivent le filtre de période.
 */
export function computeProDashboard(input: {
  commandes: ProCommandeStatRow[];
  devis: ProDevisStatRow[];
  wallet: ProWalletStatRow[];
  bounds: ProDateBounds;
}): ProDashboardSnapshot {
  const commandes = filterByRange(input.commandes, input.bounds);
  const devis = filterByRange(input.devis, input.bounds);
  return {
    wallet: computeWalletSummary(input.wallet),
    activity: computeActivitySummary(commandes, devis),
    clients: computeClientStats(commandes, devis),
  };
}
